import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { GridFilterItem } from '@mui/x-data-grid';
import { PieChart, Pie } from 'recharts';
import { v4 } from 'uuid';
import { getProjectLoadingSubject, Project } from '../../../store/Project';
import { getCurrentPreferences, setPreferences, UserPreferences } from '../../../store/User';
import { generateProjectStatusDoughnutData } from '../../../utils/helpers';
import { BORDER_STYLE } from '../../../utils/styleHelpers';
import { TextBlock, TextSpan } from '../../Atoms/Text';
import { ComponentSpinner } from '../Loading/ComponentSpinner';

export const statusOrder = ['Escalated', 'On Hold', 'In Progress', 'Installed'];

const chartWidth = 250;
const chartHeight = 132;

export interface ProjectStatusWidgetProps {
  projects: Project[];
  isHome?: boolean;
}

interface CategoricalChartState {
  name: string;
}

export const ProjectStatusWidget = ({ projects, isHome }: ProjectStatusWidgetProps) => {
  const [projectsLoading, setProjectsLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const data = generateProjectStatusDoughnutData(projects);

  useEffect(() => {
    const loadingSub = getProjectLoadingSubject().subscribe((loading) => {
      setProjectsLoading(loading);
    });

    return () => {
      if (loadingSub) loadingSub.unsubscribe();
    };
  }, []);

  const filterStatus = async (statusName: string) => {
    const filter: GridFilterItem = {
      id: v4(),
      field: 'status',
      operator: 'is',
      value: statusName
    };

    const update: UserPreferences = JSON.parse(JSON.stringify(getCurrentPreferences()));
    if (update.content.project_grid_Active.muiConfig.filter?.filterModel) {
      update.content.project_grid_Active.muiConfig.filter.filterModel.items = [filter];
    }

    setPreferences(update);
    if (isHome) navigate('/projects');
  };

  const chartClick = (status: string) => () => {
    filterStatus(status);
  };

  const pieChartClick = (chartState: CategoricalChartState) => {
    filterStatus(chartState.name);
  };

  const correctData = data.filter((item) => item.value > 0);
  const isCorrectData = correctData.length > 0;
  const gridStyles = isHome ? 'w-full' : 'min-w-64 h-52';

  return (
    <div
      className={`${BORDER_STYLE} ${gridStyles} pt-3 pr-2 bg-white`}
      data-cy="status-widget">
      <div className="col-span-2 text-sm font-medium tracking-[0.6px] text-grey-5 text-center leading-[15px]">
        Project Status Breakdown
      </div>
      {!isCorrectData && !projectsLoading && (
        <div className={`flex ${isHome ? 'h-32' : 'h-44'} items-center justify-center`}>
          <TextBlock size="sm14">No data available</TextBlock>
        </div>
      )}
      {projectsLoading && (
        <div className={`flex ${isHome ? 'h-32' : 'h-44'} items-center justify-center`}>
          <ComponentSpinner />
        </div>
      )}
      {!projectsLoading && isCorrectData && (
        <div className="flex flex-col items-center w-full">
          <div className="text-sm widget-container w-full">
            <PieChart
              width={chartWidth}
              height={chartHeight}>
              <Pie
                dataKey="value"
                data={correctData}
                cy={100}
                cx={150}
                startAngle={180}
                endAngle={0}
                innerRadius={50}
                onClick={pieChartClick}
                minAngle={5}
                paddingAngle={5}
                outerRadius={80}></Pie>
            </PieChart>
          </div>
          <div className="grid grid-cols-4 gap-1 px-2 w-full">
            {data.map((item) => (
              <StatusText
                color={item.fill}
                key={item.name}
                value={item.value}
                onClick={chartClick(item.name)}
                text={item.name}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const StatusText = ({
  text,
  color,
  value,
  onClick
}: {
  text: string;
  color: string;
  value: number;
  onClick: () => void;
}) => {
  return (
    <div
      aria-label={text}
      className="col-span-1 grey-8 font-medium leading-3 flex flex-col items-center"
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') onClick();
      }}
      role="button"
      tabIndex={0}>
      <TextBlock
        size="sm14"
        className="mb-2">
        {text}
      </TextBlock>
      <div className="flex flex-row gap-x-1.5 items-center">
        <span
          className="inline-flex w-4 h-4 rounded-full"
          style={{ backgroundColor: color }}
        />
        <TextSpan
          size="sm14"
          weight="semibold">
          {value}
        </TextSpan>
      </div>
    </div>
  );
};
